import React from "react";
import { Box, Typography, Chip, Button } from "@mui/joy";
import RefreshIcon from "@mui/icons-material/Refresh";
import { UpdateNotification } from "../../api/sse/SSE.domain";
import { useSSE } from "../../contexts/controllers/useSSE";

interface SSENotificationProps {
  topic?: string;
}

const SSENotification: React.FC<SSENotificationProps> = ({ topic = "assignments" }) => {
  const sseService = useSSE();
  const [updates, setUpdates] = React.useState<UpdateNotification[]>([]);
  const [showUpdates, setShowUpdates] = React.useState(false);

  React.useEffect(() => {
    const handleUpdate = (data: any) => {
      if (data.action && data.entity) {
        const isDuplicate = updates.some(
          (update) => update.id === data.id && update.timestamp === data.timestamp,
        );

        if (!isDuplicate) {
          setUpdates((prev) => [data, ...prev].slice(0, 5));
          setShowUpdates(true);
        }
      }
    };

    sseService.subscribe("all", handleUpdate, topic);

    return () => {
      sseService.unsubscribe("all", handleUpdate);
    };
  }, [sseService, topic, updates]);

  const formatUpdateMessage = (update: UpdateNotification) => {
    const actionText =
      update.action === "CREATE" ? "créé" : update.action === "UPDATE" ? "mis à jour" : "supprimé";

    const idText = update.externalId || update.id || "";
    return `${update.entity} ${idText} ${actionText}`;
  };

  const handleRefresh = () => {
    window.location.reload();
  };

  const handleCloseUpdates = () => {
    setShowUpdates(false);
    setUpdates([]);
  };

  if (!showUpdates || updates.length === 0) {
    return null;
  }

  return (
    <Box
      sx={{
        position: "absolute",
        top: "100%",
        left: 0,
        right: 0,
        backgroundColor: "#f8d7da",
        padding: 2,
        zIndex: 100,
        borderBottom: "1px solid #f5c6cb",
        display: "flex",
        flexDirection: "column",
        alignItems: "stretch",
        gap: 1,
      }}
    >
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
        <Typography level="title-md" color="danger">
          Mises à jour détectées
        </Typography>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            size="sm"
            color="primary"
            startDecorator={<RefreshIcon />}
            onClick={handleRefresh}
          >
            Rafraîchir la page
          </Button>
          <Button size="sm" variant="plain" color="neutral" onClick={handleCloseUpdates}>
            Fermer
          </Button>
        </Box>
      </Box>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        {updates.map((update, index) => (
          <Chip
            key={index}
            variant="soft"
            color={update.action === "CREATE" ? "success" : "primary"}
            sx={{ justifyContent: "flex-start", py: 1 }}
          >
            {update.externalId && (
              <Typography component="span" fontWeight="bold" mr={1}>
                ID: {update.externalId}
              </Typography>
            )}
            {formatUpdateMessage(update)}
          </Chip>
        ))}
      </Box>
    </Box>
  );
};

export default SSENotification;
